/**
 * Conflicts tab (plan B1, spec §3 step 6): phrases held by 2+ live intents of the bot. The bot cannot tell
 * those intents apart for that phrase, so the operator fixes it — in chatbot for synced phrases, here for
 * hand-written ones. Never tie-broken automatically.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { hubService, type IntentConflict } from '../../services/hub.service';

export function ConflictsTab({ botId, system }: { botId: string; system: string }) {
  const { getToken } = useAuth();
  const [rows, setRows] = useState<IntentConflict[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    hubService
      .listConflicts(botId, system, getToken())
      .then((data) => alive && setRows(data))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [botId, system, getToken]);

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        The same phrase belongs to more than one intent, so the bot can't tell them apart. Fix it in chatbot (synced
        phrases) or here (hand-written).
      </p>
      {rows.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500">No conflicts.</div>
      ) : (
        <table className="w-full text-sm bg-white border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-4 py-2 font-medium">Phrase</th>
              <th className="px-4 py-2 font-medium">Intents</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.text} className="border-t border-gray-100">
                <td className="px-4 py-2 text-gray-800">{r.text}</td>
                <td className="px-4 py-2">
                  <div className="flex gap-1 flex-wrap">
                    {r.intents.map((i) => (
                      <span key={i.id} className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700" title={i.name}>
                        {i.intentCode}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
